import {
  Box,
  Card,
  CardContent,
  Collapse,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useKeycloak } from "@react-keycloak/web";
import React, { forwardRef, useState, useEffect, useRef } from "react";
import PauseAudioIcon from "../../../../icons/PauseAudioIcon";
import PlayAudioIcon from "../../../../icons/PlayAudioIcon";
import { useGetMetaArtifacts } from "../../../tanstackQuery/queries/artifacts";
import Collapsible from "../../generics/components/Collapsible";
import { extractInsightGroup } from "../../insights/insight-details";
import AnalysisInsightCards from "../analysis/analysis-insights-details";
import { useInsightAudioPlayer } from "../analysis/hook/useInsightAudioPlayer";
import MaterialQuoteView from "../material-quote-view";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { baseAtom, selectInsight } from "../../../stores/jotai/base";
import { findingsAtom } from "../../../stores/jotai/insights/findings";
import { useAtom } from "jotai";

interface InsightDetailsRowProps {
  id: string;
  artifactID: string;
  name: string;
  quote: string;
  createdBy: string;
  creatorID: string;
  isSelected: boolean;
  onInsightClick: () => void;
  onDelete?: () => void;
  audioBegin?: number;
  audioEnd?: number;
  isDraft?: number;
  onAccept?: (onSuccess: () => void, onFail: (err: any) => void) => void;
  user?: any;
  childInsights?: InsightDetailsRowProps[];
  isChild?: boolean; 
  metaArtifactData:object;
}

const InsightDetailsRow = forwardRef<
  HTMLDivElement,
  InsightDetailsRowProps
>((props)  => {
  const {
    id,
    artifactID,
    name,
    quote,
    createdBy,
    creatorID,
    isSelected,
    onInsightClick,
    onDelete,
    audioBegin,
    audioEnd,
    isDraft,
    onAccept,
    user,
    childInsights,
    metaArtifactData
  } = props;

  const [base, setBase] = useAtom(baseAtom);
  const [findings, setFindings] = useAtom(findingsAtom);
  const [showChildren, setShowChildren] = useState(false);
  const rowRef = useRef<HTMLDivElement | null>(null);

  const [matchedInsightGroup, setMatchedInsightGroup] = useState<any>(undefined);
  const theme = useTheme();
  const { isPlaying, playPause } = useInsightAudioPlayer(audioBegin, audioEnd, id);

  const shouldAutoExpand = !!childInsights?.find(
    (insight) => insight.id === base?.selectedFinding?.findingID
  ) || (childInsights?.length>0 && id === base?.selectedFinding?.findingID);

  let quoteValue;
  try {
    quoteValue = JSON.parse(quote);
  } catch (e) {
    quoteValue = null;
  }

  useEffect(() => {
    if (isSelected && rowRef.current) {
      rowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isSelected]);


  useEffect(() => {
    if (shouldAutoExpand && !showChildren) {
      setShowChildren(true);
    }
  }, [shouldAutoExpand]);

  return (
    <>
      <Card
        sx={{
          my: 1,
          cursor: "pointer",
          boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
        }}
        ref={rowRef}
        onClick={() => {
          // setMatchedInsightGroup(null);
          onInsightClick();
        }}
        key={`${id}-${artifactID}-discovery-insight-table`}
      >
        <CardContent
          sx={{
            color: "black",
            borderRadius : "4px",
            px:"12px",
            background: isSelected ? (shouldAutoExpand ? "rgb(229, 246, 253)" : "#BBDEFB") : "transparent",
            border: isSelected && shouldAutoExpand ? "2px solid #1976D2" : "none",
            "&:hover": {
              backgroundColor: isSelected
                ? "#DEEBFF"
                : theme.custom.hoverOptionColor,
            },

            ".MuiSvgIcon-root": {
              color: isSelected ? "black" : "#828282",
            },
          }}
        >
          <Stack direction={"row"} gap={1.1}>
            <Collapsible>
              {({ isExpanded }) => (
                <Stack
                  direction="row"
                  sx={{
                    alignItems: isExpanded ? "start" : "center",
                  }}
                >
                  {!!audioBegin && !!audioEnd ? (
                    <Tooltip title="Play">
                      <IconButton
                        onClick={(event)=>{
                          event.stopPropagation();
                          playPause()
                        }}
                        disabled={!audioBegin || !audioEnd}
                        sx={{
                          borderColor: isSelected ? theme.custom.white : "",
                          p: "0px 0px 0px 1px",
                          ml:"5px",
                          ":hover": {
                            borderColor: isSelected
                              ? theme.custom.white70
                              : theme.custom.mainButtonHighlight,
                            color: isSelected
                              ? theme.custom.white70
                              : theme.custom.mainButtonHighlight,
                          },
                        }}
                      >
                        {isPlaying ? (
                          <PauseAudioIcon
                            selected={isSelected}
                          />
                        ) : (
                          <PlayAudioIcon
                            selected={isSelected}
                          />
                        )}
                      </IconButton>
                    </Tooltip>
                  ) : childInsights?.length > 0 ? (
                    <Tooltip
                      title={showChildren ? "Collapse" : "Show child insights"}
                    >
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowChildren((prev) => !prev);
                        }}
                        sx={{
                          borderRadius: "4px",
                          transition: "background-color 0.2s ease",
                          "&:hover": {
                            backgroundColor: "action.hover", // or use a custom color
                          },
                          p: 0,
                          ".MuiSvgIcon-root": {
                            transition: "transform 0.3s",
                          },
                        }}
                      >
                           <ArrowDropDownIcon style={{color:"black", width : "35px", height : "35px", transform: showChildren ? `rotate(0deg)`: `rotate(-90deg)`}}/> 
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Stack>
              )}
            </Collapsible>
            <Stack
              sx={{
                ".CollapsibleTypography": {
                  ":hover": {
                    color: "inherit",
                    textDecoration: "none",
                    cursor: "pointer",
                  },
                },
              }}
            >
              {quoteValue?.source === "note" || quoteValue?.source === "transcript" ? (
                <Box ml={"7px"}>
                <MaterialQuoteView
                  artifactID={artifactID}
                  selection={quote}
                  selected={isSelected}
                  quouteCollapseLength={220}
                  isInsightTable={true}
                  onReadMoreClick={onInsightClick}
                  isItalic = {false}
                  />
                  </Box>
              ) : (
                <AnalysisInsightCards
                  key={`${id}-${artifactID}`}
                  item={
                    matchedInsightGroup ?? {
                      title: quoteValue?.textCache,
                      description: quoteValue?.description,
                      insights: [],
                    }
                  }
                  showInsights={false}
                  highlightCard={false}
                  collapsible={true}
                  onExpand={async () => {
                    if (!matchedInsightGroup) {
                      const found = extractInsightGroup(id, metaArtifactData);
                      if (found) {
                        setMatchedInsightGroup(found);
                      }
                    }
                  }}
                  isSelectable={false}
                  headingStyle={{
                    fontSize:"15px"
                  }}
                />
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {childInsights?.length > 0 && (
        <Collapse in={showChildren} timeout="auto" unmountOnExit>
          <Box sx={{ p: 0, m: 0, ml: "15px" }}>
            {childInsights.map((child) => (
              <InsightDetailsRow
                key={child.id}
                {...child}
                isChild={true}
                isSelected={base.selectedFinding?.findingID === child.id}
                onInsightClick={() => {
                  selectInsight(setBase, findings?.[child.id]);
                }}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </>
  );
});

export default InsightDetailsRow;
